import { API_BASE_URL, getAuthToken } from "./client";

const parseErrorBody = async (response: Response) => {
  const text = await response.text();
  try {
    const parsed = JSON.parse(text);
    return parsed?.error || parsed?.message || text;
  } catch {
    return text;
  }
};

export function downloadDatabaseBackup(
  onProgress?: (loadedBytes: number, totalBytes: number) => void,
) {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Authentication required");
  }

  return new Promise<{
    blob: Blob;
    filename: string;
  }>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("GET", `${API_BASE_URL}/api/admin/backup`);
    request.responseType = "blob";
    request.setRequestHeader("Authorization", `Bearer ${token}`);
    request.onprogress = (event) => {
      onProgress?.(event.loaded, event.lengthComputable ? event.total : 0);
    };
    request.onload = async () => {
      if (request.status < 200 || request.status >= 300) {
        const error = await parseErrorBody(
          new Response(request.response, { status: request.status }),
        );
        reject(new Error(error || "Unable to download backup"));
        return;
      }

      const disposition =
        request.getResponseHeader("Content-Disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      resolve({
        blob: request.response,
        filename: filenameMatch
          ? filenameMatch[1]
          : `hmo-manager-backup-${new Date()
              .toISOString()
              .replace(/[:.]/g, "-")}.tar.gz`,
      });
    };
    request.onerror = () => reject(new Error("Unable to download backup"));
    request.onabort = () => reject(new Error("Backup download was cancelled"));
    request.send();
  });
}

export function restoreDatabaseBackup(
  file: File,
  onProgress?: (
    loadedBytes: number,
    totalBytes: number,
    phase: "uploading" | "processing",
  ) => void,
) {
  const token = getAuthToken();
  if (!token) {
    throw new Error("Authentication required");
  }

  const formData = new FormData();
  formData.append("backupFile", file);

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", `${API_BASE_URL}/api/admin/restore`);
    request.setRequestHeader("Authorization", `Bearer ${token}`);
    request.upload.onprogress = (event) => {
      onProgress?.(
        event.loaded,
        event.lengthComputable ? event.total : file.size,
        "uploading",
      );
    };
    request.onload = async () => {
      onProgress?.(file.size, file.size, "processing");
      if (request.status < 200 || request.status >= 300) {
        const error = await parseErrorBody(
          new Response(request.responseText, { status: request.status }),
        );
        reject(new Error(error || "Unable to restore backup"));
        return;
      }

      try {
        resolve(JSON.parse(request.responseText));
      } catch {
        reject(new Error("Unable to read restore response"));
      }
    };
    request.onerror = () => reject(new Error("Unable to restore backup"));
    request.onabort = () => reject(new Error("Backup restore was cancelled"));
    request.send(formData);
  });
}
